import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function HealthMonitor() {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/health/monitor");
      setData(data);
    } catch (err) {
      console.warn("health monitor failed:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  if (!data) {
    return (
      <div className="mq-panel p-4" data-testid="health-monitor-panel">
        <div className="mq-heading text-sm tracking-widest uppercase mb-2">{t("healthTitle")}</div>
        <div className="text-xs text-[var(--mq-text-3)]">{t("loading")}</div>
      </div>
    );
  }

  const exchanges = Object.entries(data.exchanges || {});
  return (
    <div className="mq-panel" data-testid="health-monitor-panel">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">{t("healthTitle")}</span>
        <span className="mq-label">
          {t("healthTotalPortfolio")}:{" "}
          <span className="font-mono text-[var(--mq-green)]">
            ${data.total_portfolio_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[var(--mq-border)]">
        <Kpi label={t("healthEngineRunning")} value={data.engine.running ? "YES" : "NO"}
             color={data.engine.running ? "var(--mq-green)" : "var(--mq-text-3)"} testid="health-engine-running" />
        <Kpi label={t("healthEngineUptime")} value={`${formatUptime(data.engine.uptime_sec)}`} testid="health-engine-uptime" />
        <Kpi label={t("healthBotRunning")} value={data.bot.running ? "YES" : "NO"}
             color={data.bot.running ? "var(--mq-green)" : "var(--mq-text-3)"} testid="health-bot-running" />
        <Kpi label={t("healthAlerts24h")} value={data.alerts_24h}
             color={data.alerts_24h > 0 ? "var(--mq-yellow)" : "var(--mq-text-1)"} testid="health-alerts-24h" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-left mq-label">
              <th className="px-4 py-2">{t("exchange")}</th>
              <th className="px-2 py-2">{t("healthStatus")}</th>
              <th className="px-2 py-2 text-right">{t("healthLatency")}</th>
              <th className="px-2 py-2 text-right">{t("healthLastSync")}</th>
              <th className="px-2 py-2 text-right">{t("healthPortfolio")}</th>
              <th className="px-2 py-2">{t("healthMode")}</th>
              <th className="px-4 py-2">{t("healthError")}</th>
            </tr>
          </thead>
          <tbody>
            {exchanges.length === 0 ? (
              <tr><td colSpan={7} className="p-4 text-[var(--mq-text-3)]">{t("noData")}</td></tr>
            ) : exchanges.map(([name, x]) => (
              <tr
                key={name}
                data-testid={`health-row-${name}`}
                className="border-t"
                style={{ borderColor: "var(--mq-border)" }}
              >
                <td className="px-4 py-1.5 uppercase font-semibold" style={{ color: "var(--mq-cyan)" }}>{name}</td>
                <td className="px-2 py-1.5" style={{ color: x.ccxt_status === "ok" ? "var(--mq-green)" : "var(--mq-red)" }}>
                  ● {x.ccxt_status.toUpperCase()}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {x.latency_ms !== null && x.latency_ms !== undefined ? `${x.latency_ms}ms` : "—"}
                </td>
                <td className="px-2 py-1.5 text-right text-[var(--mq-text-2)]">
                  {x.last_sync_age_sec !== null && x.last_sync_age_sec !== undefined ? `${x.last_sync_age_sec}s` : "—"}
                </td>
                <td className="px-2 py-1.5 text-right">
                  ${x.portfolio_value_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </td>
                <td className="px-2 py-1.5 uppercase" style={{ color: (x.api_connected && x.live_trading_enabled) ? "var(--mq-green)" : x.api_connected ? "var(--mq-yellow)" : "var(--mq-text-3)" }}>
                  {(x.api_connected && x.live_trading_enabled) ? "LIVE" : x.api_connected ? "READ" : x.has_credentials ? "AUTH?" : "—"}
                </td>
                <td className="px-4 py-1.5 text-[var(--mq-red)] truncate max-w-xs">
                  {x.ccxt_error || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, testid }) {
  return (
    <div className="bg-black/60 p-3" data-testid={testid}>
      <div className="mq-label">{label}</div>
      <div className="font-mono text-base font-bold" style={{ color: color || "var(--mq-text-1)" }}>{value}</div>
    </div>
  );
}

function formatUptime(s) {
  if (!s || s < 1) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
}
